"use client";

import React, { useState } from "react";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import { cn } from "@/lib/utils";
import { VybeAgentMark } from "@/components/desktop/agent/VybeAgentMark";
import { GoogleSignInButton } from "@/components/common/GoogleSignInButton";
import { useVybeAuthStore } from "@/lib/vybe-auth";

/**
 * Gates the Vybe Agent behind a Vybe Trader login (email → one-time code). Trading works without this;
 * only the LLM agent requires it. Shown in place of the chat when signed out.
 */
export function VybeSignInPanel({ variant }: { variant: "sidebar" | "mobile" }) {
  const isMobile = variant === "mobile";
  const status = useVybeAuthStore((s) => s.status);
  const error = useVybeAuthStore((s) => s.error);
  const pendingEmail = useVybeAuthStore((s) => s.pendingEmail);
  const requestCode = useVybeAuthStore((s) => s.requestCode);
  const verifyCode = useVybeAuthStore((s) => s.verifyCode);

  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const codeSent = status === "code-sent";

  const submit = async () => {
    setBusy(true);
    try {
      if (codeSent) await verifyCode(code);
      else await requestCode(email);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-background px-6 text-center">
      <VybeAgentMark size={isMobile ? 28 : 24} tone="primary" />
      <div className="space-y-1">
        <div className={cn("font-semibold", isMobile ? "text-base" : "text-sm")}>
          Sign in to Vybe Trader
        </div>
        <p className={cn("text-muted-foreground", isMobile ? "text-sm" : "text-xs")}>
          {codeSent
            ? `Enter the 6-digit code sent to ${pendingEmail}.`
            : "Log in to use the Vybe Agent. Trading works without signing in."}
        </p>
      </div>

      <div className="w-full max-w-xs space-y-2">
        {/* Google door (proposal 71) — only on the first step; once a code is out, finish that. */}
        {!codeSent ? <GoogleSignInButton /> : null}

        {!codeSent ? (
          <Input
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void submit()}
          />
        ) : (
          <Input
            inputMode="numeric"
            maxLength={6}
            placeholder="••••••"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void submit()}
            className="text-center tracking-[0.4em]"
          />
        )}

        {error ? <div className="text-[11px] text-destructive">{error}</div> : null}

        <Button
          className="w-full"
          disabled={busy || (codeSent ? code.trim().length === 0 : email.trim().length === 0)}
          onClick={() => void submit()}
        >
          {busy ? "Please wait…" : codeSent ? "Verify & continue" : "Email me a code"}
        </Button>

        {codeSent ? (
          <Button
            variant="link"
            className="text-muted-foreground"
            onClick={() => void requestCode(pendingEmail ?? email)}
          >
            Resend code
          </Button>
        ) : null}
      </div>
    </div>
  );
}
